// src/store/slices/authSlice.ts

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AdminLoginRequest, AdminLoginResponse, UserInfo } from '../../types';
import client from '../../api/client';

// ─── Estado ───────────────────────────────────────────────────────────────────

interface AuthState {
  token: string | null;
  user: UserInfo | null;
  loading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  token: localStorage.getItem('fg_admin_token'),
  user: (() => {
    try { return JSON.parse(localStorage.getItem('fg_admin_user') || 'null'); }
    catch { return null; }
  })(),
  loading: false,
  error: null,
};

// ─── Thunk de login ──────────────────────────────────────────────────────────

export const loginThunk = createAsyncThunk(
  'auth/adminLogin',
  async (payload: AdminLoginRequest, { rejectWithValue }) => {
    try {
      const res = await client.post<AdminLoginResponse>('/auth/admin/login', payload);
      return res.data;
    } catch (err: any) {
      // Captura los mensajes del backend:
      // 401 → "Credenciales incorrectas"
      // 403 → "No tienes permisos para acceder al panel administrativo"
      const data = err.response?.data;
      const msg =
        (typeof data === 'string' ? data : null) ||
        data?.mensaje ||
        data?.message ||
        data?.error ||
        'Error al iniciar sesión. Verifica tus credenciales.';
      return rejectWithValue(msg);
    }
  },
);

// ─── Slice ────────────────────────────────────────────────────────────────────

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout(state) {
      state.token = null;
      state.user  = null;
      localStorage.removeItem('fg_admin_token');
      localStorage.removeItem('fg_admin_user');
    },
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginThunk.pending, (state) => {
        state.loading = true;
        state.error   = null;
      })
      .addCase(loginThunk.fulfilled, (state, action) => {
        const p = action.payload;
        state.loading = false;
        state.token   = p.token;
        state.user    = {
          userId:    p.userId,
          email:     p.email,
          firstName: p.firstName,
          lastName:  p.lastName,
          username:  p.username,
          admin:     p.admin,
          roles:     p.roles,
        };
        localStorage.setItem('fg_admin_token', p.token);
        localStorage.setItem('fg_admin_user',  JSON.stringify(state.user));
      })
      .addCase(loginThunk.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload as string;
      });
  },
});

export const { logout, clearError } = authSlice.actions;
export default authSlice.reducer;
